import { useMemo } from "react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  cn,
  MessageTagSelector,
  AssigneeAvatarGroup,
  AssigneeAdmin,
  withKey,
} from "@tellescope/ui";
import { Archive, ArchiveX, Trash2, Clock4, EllipsisVertical, MailOpen, Mail } from "lucide-react";
import { formatDateTime, formatEmailTitle, formatName, formatPhone } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import {
  useAuth,
  useCachedTags,
  useInboxConversation,
  useUpdateThreadInboxStatus,
  useUsersQuery,
} from "@tellescope/tanstack-query";
import { useNavigate } from "react-router-dom";
import { AvatarWithPicture } from "../../../../components/AvatarWithPicture";
import { useInboxThreadViewShallowStore } from "../../../../store";
import { InboxStatusBadge } from "../InboxStatusBadge";
import { PAGE_HEADER_SIZE_STRING } from "../../../../constants/header-sizing";
import { SharedPhoneNumberToggle } from "../../../../components/SharedPhoneNumberToggle";
import { useShallowSidebarStore } from "../../../../store/sidebar-store";

interface InboxConversationHeaderProps {
  threadId: string;
  conversationData?: ReturnType<typeof useInboxConversation>;
}

/**
 * Header component for inbox conversation view
 * Displays action buttons and conversation metadata
 */
export const InboxConversationHeader = withKey(({ threadId, conversationData }: InboxConversationHeaderProps) => {
  const { t } = useTranslation("inbox");
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { openSidebar } = useShallowSidebarStore("openSidebar");

  const internalConversationData = useInboxConversation(threadId, { enabled: !conversationData });
  const {
    metadata,
    endusers,
    replyTo,
    messages,
    markReadOrUnread,
    toggleTag,
    toggleArchived,
    toggleTrashed,
    setAssignees,
    filterByEnduserId,
    setFilterByEnduserId,
  } = conversationData ?? internalConversationData;
  const { setInboxViewThreadData } = useInboxThreadViewShallowStore("setInboxViewThreadData");
  const { data: availableTags } = useCachedTags({ model: "inbox_threads" });
  const isUnread = currentUser.id ? !metadata?.readBy?.[currentUser.id] : false;

  const { updateInboxStatus } = useUpdateThreadInboxStatus({
    threadId,
    metadata,
    messages,
  });

  // Get CC recipients from the latest email message
  const ccRecipients = useMemo(() => {
    if (metadata?.type !== "Email" || !messages.length) return undefined;
    const latestMessage = messages[messages.length - 1];
    return latestMessage?.cc && latestMessage.cc.length > 0 ? latestMessage.cc : undefined;
  }, [metadata?.type, messages]);

  const enduserNames = useMemo(() => {
    const names = endusers.reduce<string[]>((acc, curr) => {
      acc.push(formatName(curr));
      return acc;
    }, []);
    return names.join(", ");
  }, [endusers]);

  const usersQuery = useUsersQuery();
  const availableAdmins = useMemo(() => {
    return (
      usersQuery.data?.map<AssigneeAdmin>((user) => ({
        id: user.id,
        name: formatName(user),
        email: user.email,
        avatar: user.avatar,
      })) ?? []
    );
  }, [usersQuery.data]);

  return (
    <div className="border-b border-b-gray-200">
      <div className={`flex justify-between items-center px-4 border-b h-[${PAGE_HEADER_SIZE_STRING}] flex-shrink-0`}>
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="icon"
            hoverLabel={metadata?.archivedAt ? t("Unarchive") : t("Archive")}
            data-trackingid={metadata?.archivedAt ? "inbox-conversation-unarchive" : "inbox-conversation-archive"}
            onClick={() =>
              toggleArchived({
                onSuccess: () => {
                  navigate("../..", { replace: true });
                },
              })
            }
          >
            {metadata?.archivedAt ? <ArchiveX className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            hoverLabel={metadata?.trashedAt ? t("Restore") : t("Move to Trash")}
            data-trackingid={metadata?.trashedAt ? "inbox-conversation-restore" : "inbox-conversation-trash"}
            onClick={() => {
              toggleTrashed({
                onSuccess: () => {
                  navigate("../..", { replace: true });
                },
              });
            }}
          >
            {metadata?.trashedAt ? <Clock4 className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />}
          </Button>
        </div>
        <div className="flex items-center space-x-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-trackingid="inbox-conversation-actions-menu">
                <EllipsisVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {isUnread ? (
                <DropdownMenuItem
                  onClick={() => {
                    if (!currentUser || !metadata) return;
                    setInboxViewThreadData(metadata.type.toLowerCase(), undefined);
                    markReadOrUnread({
                      userId: currentUser.id,
                      readStatus: "read",
                    });
                  }}
                >
                  <MailOpen className="mr-2 h-4 w-4" />
                  {t("Mark as Read")}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={() => {
                    if (!currentUser) return;
                    markReadOrUnread({
                      userId: currentUser.id,
                      readStatus: "unread",
                    });
                  }}
                >
                  <Mail className="mr-2 h-4 w-4" />
                  {t("Mark as Unread")}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="flex justify-between items-start px-4 pt-4 pb-2 flex-shrink-0">
        <div className="flex items-start space-x-4">
          <div className="flex gap-0">
            {endusers.map((enduser, i) => (
              <AvatarWithPicture
                key={`avatar-${enduser.id}`}
                className={cn(
                  "w-10 h-10 rounded-full bg-muted z-[10] outline-white outline-2 ",
                  i > 0 && `ml-[-15px] z-[${10 - i}]`
                )}
                avatarLike={enduser}
              />
            ))}
          </div>

          <div className="space-y-1">
            <h3 className="text-sm font-semibold">
              {endusers.map((enduser, index) => (
                <span key={enduser.id}>
                  <Button
                    variant="link"
                    size={null}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      openSidebar("ContactDetails", {
                        enduserId: enduser.id,
                        selectedSection: "Information",
                      });
                    }}
                    className="h-auto p-0 text-sm font-semibold underline-offset-4"
                  >
                    {formatName(enduser)}
                  </Button>
                  {index < endusers.length - 1 && ", "}
                </span>
              ))}
            </h3>
            <p className="text-xs">
              {metadata?.type === "SMS"
                ? formatPhone(metadata.title)
                : metadata?.type === "Email"
                ? formatEmailTitle(metadata?.title ?? "")
                : metadata?.title}
            </p>
            {!!replyTo && <p className="text-xs font-medium">{t("Reply-To: {{replyTo}}", { replyTo })}</p>}
            {!!ccRecipients && (
              <p className="text-xs font-medium text-muted-foreground">
                {t("cc: {{cc}}", { cc: ccRecipients.join(", ") })}
              </p>
            )}
            <div className="mt-2 h-8 flex items-center gap-2">
              <MessageTagSelector
                availableTags={availableTags}
                selectedTags={metadata?.tags ?? []}
                onTagSelect={(tagName) => {
                  toggleTag(tagName);
                }}
              />
              {!!metadata?.inboxStatus && (
                <InboxStatusBadge
                  inboxStatus={metadata.inboxStatus}
                  resolveButtonPlacement="right"
                  onStatusChange={(status) =>
                    updateInboxStatus({
                      inboxStatus: status,
                    })
                  }
                />
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end space-y-2">
          {metadata?.timestamp !== undefined && (
            <span className="text-xs text-muted-foreground">
              {formatDateTime(metadata.timestamp, "MM/DD/YYYY at hh:mm a")}
            </span>
          )}
          <AssigneeAvatarGroup
            availableAdmins={availableAdmins}
            assignees={metadata?.assignedTo ?? []}
            disablePrimaryChange
            onAssigneeChange={async (newAssignees: string[]) => {
              if (
                metadata?.assignedTo.length !== newAssignees.length ||
                !newAssignees.every((id) => metadata.assignedTo.includes(id))
              ) {
                await setAssignees(newAssignees);
              }
            }}
            label={t("Assign users to the thread")}
          />
        </div>
      </div>
      {metadata?.type === "SMS" && (
        <SharedPhoneNumberToggle
          phoneNumber={metadata?.enduserPhoneNumber ?? ""}
          contactName={enduserNames}
          showAll={!filterByEnduserId}
          onToggle={(newVal) => setFilterByEnduserId(!newVal)}
          className="mb-2 px-4"
        />
      )}
    </div>
  );
}, "threadId");
